import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Divider } from 'antd';

import { useConsumable } from '@/core/domain/operational/Consumables/Consumables.hooks';
import { useConsumableStockPrices } from '@/core/domain/stock/StockItems/StockItems.hooks';
import { ConsumableStockPricesResult } from '@/core/domain/stock/StockItems/StockItems.types';

import { quantityFormatter } from '@/utils/number';

import { PageProps } from '@/types/PageProps';

import { useUpdateBreadcrumbParam } from '@/contexts/BreadcrumbContextProvider';

import {
  DefaultRouteTabs,
  ColumnsType,
  Table,
  EditIcon,
  SaveButton,
} from '@/components/_common';
import ConsumableDetailsDescription from '@/components/Operational/Inventory/Consumables/ConsumableDetailsDescription';
import CompanyConsumableStockPricesModal from '@/components/Operational/Inventory/Consumables/CompanyConsumableStockPricesModal';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { useConsumableFormContext } from '../ConsumableProvider';
import ConsumableFooter from '@/components/Operational/Inventory/Consumables/ConsumableFooter';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

function ConsumableStockPage({ route }: PageProps) {
  const intl = useIntl();

  const {
    handleSubmit,
    isLoading: contextLoading,
    disabled,
    consumableId,
  } = useConsumableFormContext();

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedConsumableStockPrices, setSelectedConsumableStockPrices] =
    useState<ConsumableStockPricesResult>();

  const {
    data: consumable,
    isLoading: isLoadingConsumable,
    refetch: refetchPrices,
  } = useConsumable(+consumableId);

  useUpdateBreadcrumbParam(':consumableId', consumable?.name);

  const { data, isLoading } = useConsumableStockPrices(+consumableId);

  const handleModalVisible = (item?: ConsumableStockPricesResult) => {
    if (item) {
      setSelectedConsumableStockPrices(item);
    } else {
      setSelectedConsumableStockPrices(undefined);

      refetchPrices();
    }

    setModalVisible(!modalVisible);
  };

  const columns: ColumnsType<ConsumableStockPricesResult> = [
    {
      dataIndex: ['company', 'name'],
      title: intl.formatMessage({ id: 'parts.stock.domain.company' }),
      ellipsis: true,
      width: COLUMNS_WIDTH.default,
    },
    {
      dataIndex: 'stock',
      title: intl.formatMessage({ id: 'parts.stock.domain.stock' }),
      align: 'right',
      width: COLUMNS_WIDTH.default,

      render: (value: number) => quantityFormatter(value),
    },
    {
      dataIndex: 'availableStock',
      title: intl.formatMessage({ id: 'parts.stock.domain.availableStock' }),
      align: 'right',
      ellipsis: true,
      width: 140,
      render: (value: number) => quantityFormatter(value),
    },
    {
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      width: 50,
      fixed: 'right',
      align: 'right',
      render: (_, record) => (
        <EditIcon
          disabled={
            !hasPermissions([Authorities.REGISTER_CONSUMABLE_EDIT_STOCK])
          }
          iconProps={{ onClick: () => handleModalVisible(record) }}
        />
      ),
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <DefaultRouteTabs
        route={route.parent}
        baseKey={consumableId}
        basePath={`/app/operational/inventory/consumables/${consumableId}`}
        extra={
          <SaveButton
            block
            onClick={handleSubmit}
            loading={contextLoading}
            disabled={
              disabled ||
              !hasPermissions([Authorities.REGISTER_CONSUMABLE_EDIT])
            }
          >
            {intl.formatMessage({ id: 'commons.entity.save' })}
          </SaveButton>
        }
      >
        <ConsumableDetailsDescription
          isLoading={isLoadingConsumable}
          consumable={consumable}
        />

        <Divider />

        <Table<ConsumableStockPricesResult>
          columns={columns}
          dataSource={data?.result}
          loading={isLoading}
          pagination={false}
          rowKey={(row) => row.company.id}
          scroll={{ x: getTableScrollX(columns) }}
        />

        <ConsumableFooter disabled={disabled} />

        <CompanyConsumableStockPricesModal
          visible={modalVisible}
          handleVisible={handleModalVisible}
          consumableStockPrices={selectedConsumableStockPrices}
        />
      </DefaultRouteTabs>
    </>
  );
}

export default ConsumableStockPage;
